<template>
    <v-container fluid>
        <v-card>
            <!-- Main content -->
            <v-card-text class="pa-6">
                <v-form ref="edit_form" lazy-validation>
                    <!-- Row 1: Basic Info + Images/Specs/Pricing -->
                    <v-row>
                        <!-- Left column - Basic Info -->
                        <v-col cols="12" md="7">
                            <!-- Basic Information Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-information</v-icon>
                                    {{ $t('dialog.basic_info') }}
                                    <v-spacer></v-spacer>
                                    <v-chip dark :color="display_color(currentItem.Status, product_status_list)">{{
                                        currentItem.Status
                                        | display_value(product_status_list) }}</v-chip>
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete outlined dense v-model="currentItem.UID" :items="customers"
                                                item-value="ID" item-text="Username" :label="$t('form.select_customer')"
                                                autocomplete="off" @change="onCustomerChange"
                                                prepend-inner-icon="mdi-account"></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field outlined dense :label="$t('form.partner_name')"
                                                v-model="currentItem.PartnerName"
                                                prepend-inner-icon="mdi-domain"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete outlined dense v-model="currentItem.StoreID"
                                                :items="storages" item-value="ID" item-text="Name"
                                                :label="$t('form.select_storage')" autocomplete="off"
                                                prepend-inner-icon="mdi-store"></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field outlined dense :label="$t('form.box_code')"
                                                v-model="currentItem.BoxCode"
                                                prepend-inner-icon="mdi-package-variant"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete outlined dense v-model="currentItem.ProductType"
                                                :items="product_types" item-value="ID" item-text="Name"
                                                :label="$t('form.product_type')" autocomplete="off"
                                                prepend-inner-icon="mdi-shape"></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field outlined dense :label="$t('form.product_name_cn')"
                                                v-model="currentItem.ProductNameCN"
                                                prepend-inner-icon="mdi-information"></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <h-currency-input :label="$t('form.exchange_rate')"
                                                v-model="currentItem.ExchangeRate" :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete outlined dense v-model="selectedQuotes"
                                                :items="availableQuotes" item-value="ID"
                                                :item-text="quote => `${quote.ID || 'N/A'} - HSCODE: ${quote.HsCode || 'N/A'} - ${quote.ProductInfo || $t('form.no_name')}`"
                                                :item-disabled="item => !isQuoteSelectable(item.ID)"
                                                :label="$t('form.select_quote')" autocomplete="off"
                                                :loading="loadingQuotes" :disabled="!currentItem.UID"
                                                :filter="quoteFilter" clearable multiple chips deletable-chips
                                                small-chips
                                                :no-data-text="currentItem.UID ? $t('form.no_quote') : $t('form.select_customer_first')"
                                                prepend-inner-icon="mdi-file"
                                                :hint="selectedQuotes.length > 0 ? `Đã chọn ${selectedQuotes.length} khai báo` : ''"
                                                persistent-hint>
                                                <template v-slot:selection="{ item, index }">
                                                    <v-chip v-if="index < 3" close small color="primary"
                                                        text-color="white" @click:close="removeQuote(item.ID)">
                                                        {{ item.ID }} - {{ (item.ProductInfo || '').substring(0, 20)
                                                        }}{{ (item.ProductInfo || '').length > 20 ? '...' : '' }}
                                                    </v-chip>
                                                    <span v-if="index === 3" class="grey--text caption ml-2">
                                                        (+{{ selectedQuotes.length - 3 }} khác)
                                                    </span>
                                                </template>
                                                <template v-slot:item="{ item, attrs, on }">
                                                    <v-list-item v-bind="attrs" v-on="on"
                                                        :class="{ 'disabled-quote-item': isQuoteLinked(item.ID) }">
                                                        <v-list-item-content>
                                                            <v-list-item-title>
                                                                {{ $t('table_headers.quote_id') }}: <strong>{{ item.ID
                                                                    || 'N/A' }}</strong> - {{ item.ProductInfo ||
                                                                        $t('form.no_name') }}
                                                            </v-list-item-title>
                                                            <v-list-item-subtitle>
                                                                {{ $t('form.total_amount') }}: {{ item.TotalAmount |
                                                                    currency }} - {{ $t('form.created_date') }}: {{
                                                                    item.CreatedDate | display_date('DD/MM/YYYY') }} <span
                                                                    v-if="item.IsLinked" class="red--text">- (Đã liên
                                                                    kết)</span>
                                                            </v-list-item-subtitle>
                                                        </v-list-item-content>
                                                        <v-list-item-avatar v-if="item.ImageUrl">
                                                            <v-img
                                                                :src="image_proxy + encodeURIComponent(item.ImageUrl) + '&w=48&h=48'"
                                                                max-width="48" max-height="48"></v-img>
                                                        </v-list-item-avatar>
                                                    </v-list-item>
                                                </template>
                                            </v-autocomplete>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-text-field outlined dense :label="$t('form.note')"
                                                v-model="currentItem.Note"
                                                prepend-inner-icon="mdi-note-text"></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Cost and Tax Information Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-calculator</v-icon>
                                    {{ $t('dialog.cost_tax_info') }}
                                    <v-spacer></v-spacer>
                                    <!-- <v-checkbox
                                        :label="$t('table_headers.is_split_tax')"
                                        v-model="currentItem.IsSplitTax"
                                    ></v-checkbox> -->
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="3">
                                            <h-currency-input :label="$t('form.weight_cost')"
                                                v-model="currentItem.WeightCost" :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <h-currency-input :label="$t('form.volume_cost')"
                                                v-model="currentItem.VolumeCost" :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <h-currency-input :label="$t('form.subtotal')"
                                                v-model="currentItem.TotalAmount" :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            <h-currency-input :label="$t('form.total_profit')"
                                                v-model="currentItem.TotalProfit" :decimal="0"
                                                readonly></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <!-- Right column - Images, Specs, Pricing -->
                        <v-col cols="12" md="5">
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-image-multiple</v-icon>
                                    {{ $t('form.image') }}
                                </v-card-title>
                                <v-card-text @paste="handlePaste">
                                    <input v-if="!uploading" type="file" ref="fileInput" @change="uploadFile"
                                        class="mb-3">
                                    <v-progress-circular v-if="uploading > 0" indeterminate size="32"
                                        class="mb-3"></v-progress-circular>

                                    <div class="d-flex flex-wrap">
                                        <div v-for="(f, index) in files" :key="f.url" class="mr-2 mb-2"
                                            style="position:relative; display:inline-block;">
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on }">
                                                    <v-img v-on="on"
                                                        :src="f.local === 1 ? f.url : (image_proxy + encodeURIComponent(f.url) + '&w=150&h=150')"
                                                        width="150" height="150" aspect-ratio="1"
                                                        class="rounded thumbnail-img" @click="fileOpen(f)"
                                                        style="cursor:pointer; border:1px solid #eee;"></v-img>
                                                </template>
                                                <v-img
                                                    :src="f.local === 1 ? f.url : (image_proxy + encodeURIComponent(f.url) + '&w=300')"
                                                    max-width="300"></v-img>
                                            </v-tooltip>
                                            <v-btn icon x-small color="error"
                                                style="position:absolute;top:4px;right:4px;z-index:2;"
                                                @click="fileRemove(index)">
                                                <v-icon x-small>mdi-close-circle</v-icon>
                                            </v-btn>
                                            <div class="caption text-truncate mt-1"
                                                style="max-width:150px;font-size:11px;text-align:center;">
                                                {{ f.name }}
                                            </div>
                                        </div>
                                    </div>

                                    <v-alert v-if="files.length === 0" text color="grey" class="mt-3">
                                        <div class="text-center">
                                            <v-icon large color="grey lighten-1">mdi-image-off</v-icon>
                                            <div class="mt-2">{{ $t('form.no_images') }}</div>
                                            <div class="caption mt-1 grey--text">Bạn có thể dán ảnh từ clipboard (Ctrl+V
                                                / Cmd+V)</div>
                                        </div>
                                    </v-alert>
                                </v-card-text>
                            </v-card>
                            <!-- Product Specifications Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-cube-outline</v-icon>
                                    {{ $t('dialog.product_specs') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6" md="4">
                                            <h-currency-input :label="$t('form.weight')" v-model="currentItem.Weight"
                                                :decimal="1"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <h-currency-input :label="$t('form.volume')" v-model="currentItem.Volume"
                                                :decimal="3"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="4">
                                            <h-currency-input :label="$t('form.number_of_pack')"
                                                v-model="currentItem.NumberOfPack" :decimal="0"></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Pricing Information Section -->
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-tag</v-icon>
                                    {{ $t('dialog.pricing_info') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete outlined dense v-model="currentItem.PriceType"
                                                :items="price_types" item-value="value" item-text="name"
                                                :label="$t('form.price_type')" autocomplete="off"></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input :label="$t('form.weight_price')"
                                                v-model="currentItem.WeightPrice" :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <h-currency-input :label="$t('form.volume_price')"
                                                v-model="currentItem.VolumePrice" :decimal="0"></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-file-document-outline</v-icon>
                                    Giá trị đơn hàng hóa
                                    <v-spacer></v-spacer>
                                    <v-checkbox label="Đã back hóa đơn" v-model="currentItem.IsBack"></v-checkbox>
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="2">
                                            <h-currency-input label="Số lượng" v-model="currentItem.InvoiceQuantity"
                                                :decimal="2"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <h-currency-input label="Đơn giá" v-model="currentItem.InvoiceUnitPrice"
                                                :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <h-currency-input label="Thành tiền"
                                                v-model="currentItem.InvoiceTotalAmount"
                                                :decimal="0"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <h-currency-input label="VAT (%)" v-model="currentItem.InvoiceVatRate"
                                                :decimal="1"></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <h-currency-input label="Thành tiền sau VAT"
                                                v-model="currentItem.InvoiceTotalAfterVat" :decimal="0"
                                                readonly></h-currency-input>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <div class="d-flex justify-end mt-2">
                                                <input type="file" ref="backBillInput" accept="image/*" class="d-none"
                                                    @change="uploadBackBillImage">
                                                <v-btn v-if="!currentItem.BackBillImage" small color="primary"
                                                    @click="$refs.backBillInput.click()" :loading="backBillUploading">
                                                    <v-icon left small>mdi-upload</v-icon>
                                                    Upload bill
                                                </v-btn>
                                                <div v-else class="back-bill-thumb-wrapper">
                                                    <v-img :src="currentItem.BackBillImage" width="36" height="50"
                                                        class="rounded" style="cursor: pointer; border:1px solid #eee;"
                                                        @click="openBackBillImage"></v-img>
                                                    <v-btn icon x-small color="error" class="back-bill-remove-btn"
                                                        @click.stop="removeBackBillImage">
                                                        <v-icon x-small>mdi-close-circle</v-icon>
                                                    </v-btn>
                                                </div>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                    <!-- Row 2: Chi kho TQ - full width -->
                    <v-row>
                        <v-col cols="12">
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-cash-multiple</v-icon>
                                    {{ $t('dialog.fee_info') }}
                                    <v-spacer></v-spacer>
                                    <v-btn small color="primary" @click="addFeeItem"
                                        :disabled="readOnly && !(adminRole || csRole || storeManagerRole)" class="ml-2">
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $t('actions.add_fee') }}
                                    </v-btn>
                                </v-card-title>
                                <v-card-text class="pt-0">
                                    <v-data-table :headers="fee_headers" :items="currentFees" hide-default-footer
                                        disable-pagination class="elevation-0">
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td class="truncate"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        getDisplayText(item.FeeType, feeCnyList) }}</td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.FeeType" large lazy
                                                        @save="updateFeeItem">
                                                        <div>{{ getDisplayText(item.FeeType, feeCnyList) }}</div>
                                                        <template v-slot:input>
                                                            <v-select v-model="item.FeeType" :items="feeCnyList"
                                                                item-text="name" item-value="value" label="Tên chi phí"
                                                                autofocus></v-select>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.Currency === 'VND' ? 'VNĐ' : '¥' }}</td>
                                                <td class="text-center" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Currency" large lazy
                                                        @save="updateFeeItem">
                                                        <div>{{ item.Currency === 'VND' ? 'VNĐ' : '¥' }}</div>
                                                        <template v-slot:input>
                                                            <v-select v-model="item.Currency"
                                                                :items="[{ text: 'VNĐ', value: 'VND' }, { text: 'Yuan (¥)', value: 'CNY' }]"
                                                                item-text="text" item-value="value" label="Tiền tệ"
                                                                autofocus></v-select>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.Cost |
                                                        currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Cost" large lazy
                                                        @save="updateFeeItem">
                                                        <div>{{ item.Cost | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Cost" :autoEmit="true"
                                                                :decimal="1" label="Chi phí gốc"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.FeeAmount
                                                        | currency('', 1) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.FeeAmount" large lazy
                                                        @save="updateFeeItem">
                                                        <div>{{ item.FeeAmount | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.FeeAmount" :autoEmit="true"
                                                                :decimal="1" label="Tiền thu khách"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="truncate"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.Note }}
                                                </td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Note" large lazy
                                                        @save="updateFeeItem">
                                                        <div>{{ item.Note }}</div>
                                                        <template v-slot:input>
                                                            <v-text-field v-model="item.Note" :label="$t('form.note')"
                                                                autofocus></v-text-field>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center">
                                                    <v-icon small @click="deleteFeeItem(index)"
                                                        v-if="!readOnly || adminRole || csRole || storeManagerRole"
                                                        color="error">mdi-delete</v-icon>
                                                </td>
                                            </tr>
                                        </template>
                                        <template v-slot:[`body.append`]>
                                            <tr class="summary-row">
                                                <td class="font-weight-bold">TỔNG CHI PHÍ</td>
                                                <td class="text-center font-weight-bold">VNĐ</td>
                                                <td class="text-end font-weight-bold">{{ currentItem.OtherCost |
                                                    currency
                                                    }}</td>
                                                <td class="text-end font-weight-bold">{{ currentItem.OtherFee | currency
                                                }}</td>
                                                <td class="text-end font-weight-bold"></td>
                                                <td class="text-end font-weight-bold"></td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Row 3: Chi phí phát sinh - full width -->
                    <v-row>
                        <v-col cols="12">
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-cash-plus</v-icon>
                                    Chi phí phát sinh (hàng chính ngạch)
                                    <v-spacer></v-spacer>
                                    <v-btn small color="primary" @click="addAdditionalFeeItem"
                                        :disabled="readOnly && !(adminRole || csRole || storeManagerRole)" class="ml-2">
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $t('actions.add_fee') }}
                                    </v-btn>
                                </v-card-title>
                                <v-card-text class="pt-0">
                                    <v-data-table :headers="additional_fee_headers" :items="additionalFees"
                                        hide-default-footer disable-pagination class="elevation-0">
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td class="truncate"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        getDisplayText(item.FeeType, feeAdditionalList) }}</td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.FeeType" large lazy
                                                        @save="updateAdditionalFeeItem">
                                                        <div>{{ getDisplayText(item.FeeType, feeAdditionalList) }}</div>
                                                        <template v-slot:input>
                                                            <v-autocomplete v-model="item.FeeType" :items="feeAdditionalList"
                                                                item-text="name" item-value="value" label="Tên chi phí"
                                                                autofocus></v-autocomplete>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.Currency === 'VND' ? 'VNĐ' : '¥' }}</td>
                                                <td class="text-center" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Currency" large lazy
                                                        @save="updateAdditionalFeeItem">
                                                        <div>{{ item.Currency === 'VND' ? 'VNĐ' : '¥' }}</div>
                                                        <template v-slot:input>
                                                            <v-select v-model="item.Currency"
                                                                :items="[{ text: 'VNĐ', value: 'VND' }, { text: 'Yuan (¥)', value: 'CNY' }]"
                                                                item-text="text" item-value="value" label="Tiền tệ"
                                                                autofocus></v-select>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.Cost |
                                                        currency('', 0) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Cost" large lazy
                                                        @save="updateAdditionalFeeItem">
                                                        <div>{{ item.Cost | currency('', 0) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Cost" :autoEmit="true"
                                                                :decimal="0" label="Chi phí gốc"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.FeeAmount
                                                        | currency('', 0) }}</td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.FeeAmount" large lazy
                                                        @save="updateAdditionalFeeItem">
                                                        <div>{{ item.FeeAmount | currency('', 0) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.FeeAmount" :autoEmit="true"
                                                                :decimal="0" label="Tiền thu khách"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="truncate"
                                                    v-if="readOnly && !(adminRole || csRole || storeManagerRole)">{{
                                                        item.Note }}
                                                </td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Note" large lazy
                                                        @save="updateAdditionalFeeItem">
                                                        <div>{{ item.Note }}</div>
                                                        <template v-slot:input>
                                                            <v-text-field v-model="item.Note" :label="$t('form.note')"
                                                                autofocus></v-text-field>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center">
                                                    <v-icon small @click="deleteAdditionalFeeItem(index)"
                                                        v-if="!readOnly || adminRole || csRole || storeManagerRole"
                                                        color="error">mdi-delete</v-icon>
                                                </td>
                                            </tr>
                                        </template>
                                        <template v-slot:[`body.append`]>
                                            <tr class="summary-row">
                                                <td class="font-weight-bold">TỔNG CHI PHÍ PHÁT SINH</td>
                                                <td class="text-center font-weight-bold"></td>
                                                <td class="text-end font-weight-bold">{{ totalAdditionalCosts | currency
                                                }}</td>
                                                <td class="text-end font-weight-bold">{{ totalAdditionalFees | currency
                                                }}</td>
                                                <td class="text-end font-weight-bold"></td>
                                                <td class="text-end font-weight-bold"></td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Row 4: Chi tiết kiện hàng - full width -->
                    <v-row>
                        <v-col cols="12">
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-package-variant-closed</v-icon>
                                    {{ $t('dialog.package_details') }}
                                    <v-spacer></v-spacer>
                                    <v-btn small color="primary" @click="addSubItem"
                                        :disabled="readOnly && !(adminRole || csRole || storeManagerRole)"
                                        v-if="adminRole || csRole || storeManagerRole">
                                        <v-icon left small>mdi-plus</v-icon>
                                        {{ $t('actions.add_package') }}
                                    </v-btn>
                                </v-card-title>
                                <v-card-text class="pt-0">
                                    <v-data-table :headers="sub_headers" :items="currentPackages" hide-default-footer
                                        disable-pagination class="elevation-0">
                                        <template v-slot:item="{ item, index }">
                                            <tr>
                                                <td class="truncate" v-if="readOnly">{{ item.TransactionCode }}</td>
                                                <td class="truncate" v-else>
                                                    <v-edit-dialog :return-value.sync="item.TransactionCode" large lazy
                                                        @save="updateSubItem(true)">
                                                        <div>{{ item.TransactionCode }}</div>
                                                        <template v-slot:input>
                                                            <v-text-field v-model="item.TransactionCode"
                                                                :label="$t('form.suffix_bill')"
                                                                autofocus></v-text-field>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Length | currency('', 1) }}
                                                </td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Length" large lazy
                                                        @save="updateSubItem">
                                                        <div>{{ item.Length | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Length" :autoEmit="true"
                                                                :decimal="1" :label="$t('form.length') + '(cm)'"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Width | currency('', 1) }}
                                                </td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Width" large lazy
                                                        @save="updateSubItem">
                                                        <div>{{ item.Width | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Width" :autoEmit="true"
                                                                :decimal="1" :label="$t('form.width') + '(cm)'"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Height | currency('', 1) }}
                                                </td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Height" large lazy
                                                        @save="updateSubItem">
                                                        <div>{{ item.Height | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Height" :autoEmit="true"
                                                                :decimal="1" :label="$t('form.height') + '(cm)'"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.Weight | currency('', 1) }}
                                                </td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.Weight" large lazy
                                                        @save="updateSubItem">
                                                        <div>{{ item.Weight | currency('', 1) }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.Weight" :autoEmit="true"
                                                                :decimal="1" :label="$t('form.weight') + '(kg)'"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-end" v-if="readOnly">{{ item.NumberOfPack | currency }}
                                                </td>
                                                <td class="text-end" v-else>
                                                    <v-edit-dialog :return-value.sync="item.NumberOfPack" large lazy
                                                        @save="updateSubItem">
                                                        <div>{{ item.NumberOfPack | currency }}</div>
                                                        <template v-slot:input>
                                                            <h-currency-input v-model="item.NumberOfPack"
                                                                :autoEmit="true" :decimal="0"
                                                                :label="$t('form.number_of_pack')"
                                                                autofocus></h-currency-input>
                                                        </template>
                                                    </v-edit-dialog>
                                                </td>
                                                <td class="text-center">
                                                    <v-icon small @click="deleteSubItem(index)" v-if="!readOnly"
                                                        color="error">mdi-delete</v-icon>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-dialog v-model="backBillPreview" max-width="960">
                <v-card>
                    <v-img v-if="currentItem && currentItem.BackBillImage" :src="currentItem.BackBillImage" contain
                        max-height="80vh"></v-img>
                </v-card>
            </v-dialog>
        </v-card>
    </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "@/configs";
import fileUploadService from '@/services/fileUploadService'
import { sendErrorNotice, sendSuccessNotice } from "@/commons/utils";
import { display_color } from "@/commons/formater"
import _ from "lodash";
import storeManager from "../../store/modules/storeManager";

export default {
    name: "ProductDetail",
    data() {
        return {
            saving: false,
            image_proxy: configs.IMAGE_PROXY,
            price_types: configs.PRICE_TYPE_LIST,
            product_status_list: configs.PRODUCT_STATUS_LIST,
            sub_headers: [
                {
                    text: this.$t("form.suffix_bill"),
                    value: "TransactionCode",
                    sortable: false,
                },
                {
                    text: this.$t("form.length") + "(cm)",
                    value: "Length",
                    align: "end",
                },
                {
                    text: this.$t("form.width") + "(cm)",
                    value: "Width",
                    align: "end",
                },
                {
                    text: this.$t("form.height") + "(cm)",
                    value: "Height",
                    align: "end",
                },
                {
                    text: this.$t("form.weight") + "(kg)",
                    value: "Weight",
                    align: "end",
                },
                {
                    text: this.$t("form.number_of_pack"),
                    value: "NumberOfPack",
                    align: "end",
                },
                { text: "", value: "action", align: "center", sortable: false },
            ],
            files: [],
            uploading: 0,
            backBillUploading: false,
            backBillPreview: false,
            pendingUploads: [],
            currentPackages: [],
            currentFees: [],
            additionalFees: [],
            availableQuotes: [],
            loadingQuotes: false,
            selectedQuotes: [],  // Array of selected quote IDs
            initialSelectedQuotes: [],  // Array of initially selected quote IDs from database
            feeCnyList: configs.FEE_CNY_LIST,
            feeAdditionalList: configs.FEE_ADDITIONAL_LIST || configs.FEE_CNY_LIST,
            additional_fee_headers: [
                {
                    text: "Tên chi phí",
                    value: "FeeName",
                    sortable: false,
                    width: "200px"
                },
                {
                    text: "Tiền tệ",
                    value: "Currency",
                    sortable: false,
                    align: "center",
                    width: "120px"
                },
                {
                    text: "Chi phí gốc",
                    value: "Cost",
                    align: "end",
                    width: "130px"
                },
                {
                    text: "Tiền thu khách",
                    value: "FeeAmount",
                    align: "end",
                    width: "160px"
                },
                {
                    text: "Ghi chú",
                    value: "Note",
                    sortable: false,
                },
                { text: "Thao tác", value: "action", align: "center", sortable: false, width: "100px" },
            ],
            fee_headers: [
                {
                    text: "Tên chi phí",
                    value: "FeeName",
                    sortable: false,
                    width: "200px"
                },
                {
                    text: "Tiền tệ",
                    value: "Currency",
                    sortable: false,
                    align: "center",
                    width: "120px"
                },
                {
                    text: "Chi phí gốc",
                    value: "Cost",
                    align: "end",
                    width: "130px"
                },
                {
                    text: "Tiền thu khách",
                    value: "FeeAmount",
                    align: "end",
                    width: "160px"
                },
                {
                    text: "Ghi chú",
                    value: "Note",
                    sortable: false,
                },
                { text: "Thao tác", value: "action", align: "center", sortable: false, width: "100px" },
            ],
        };
    },
    computed: {
        ...mapState({
            currentItem: (state) => state.product.selected,
            customers: (state) => state.commons.customers,
            storages: (state) => state.storage.all.data,
            product_types: (state) => state.productType.all.data,
        }),
        adminRole() {
            return (
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Admin ||
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Accountant
            );
        },
        csRole() {
            return (
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.CustomerSupport ||
                this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.StoreVN
            );
        },
        storeManagerRole() {
            return this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.StoreManager;
        },
        readOnly() {
            return (
                this.currentItem &&
                this.currentItem.ID > 0 &&
                !(
                    this.currentItem.Status == configs.PRODUCT_STATUS.nhap_kho_tq ||
                    this.currentItem.Status == configs.PRODUCT_STATUS.cho_xep_xe
                )
                && !(this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Admin ||
                    this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Accountant ||
                    this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.CustomerSupport)
            );
        },
        totalOtherFees() {
            var exchangeRate = 1;
            if (this.currentItem && this.currentItem.ExchangeRate) {
                exchangeRate = this.currentItem.ExchangeRate;
            }
            return _.sumBy(this.currentFees, function (fee) {
                var amount = fee.FeeAmount || 0;
                return fee.Currency === 'VND' ? amount : amount * exchangeRate;
            }) || 0;
        },
        totalOtherCosts() {
            var exchangeRate = 1;
            if (this.currentItem && this.currentItem.ExchangeRate) {
                exchangeRate = this.currentItem.ExchangeRate;
            }
            return _.sumBy(this.currentFees, function (fee) {
                var cost = fee.Cost || 0;
                return fee.Currency === 'VND' ? cost : cost * exchangeRate;
            }) || 0;
        },
        totalAdditionalFees() {
            var exchangeRate = 1;
            if (this.currentItem && this.currentItem.ExchangeRate) {
                exchangeRate = this.currentItem.ExchangeRate;
            }
            return _.sumBy(this.additionalFees, function (fee) {
                var amount = fee.FeeAmount || 0;
                return fee.Currency === 'VND' ? amount : amount * exchangeRate;
            }) || 0;
        },
        totalAdditionalCosts() {
            var exchangeRate = 1;
            if (this.currentItem && this.currentItem.ExchangeRate) {
                exchangeRate = this.currentItem.ExchangeRate;
            }
            return _.sumBy(this.additionalFees, function (fee) {
                var cost = fee.Cost || 0;
                return fee.Currency === 'VND' ? cost : cost * exchangeRate;
            }) || 0;
        },

    },
    watch: {
        'currentItem.ID': {
            handler(newId, oldId) {
                if (newId !== oldId && this.currentItem) {
                    // Load selected quotes only when ID changes (new product loaded)
                    const val = this.currentItem;
                    if (val.QuoteIDs && Array.isArray(val.QuoteIDs)) {
                        this.selectedQuotes = [...val.QuoteIDs];
                        this.initialSelectedQuotes = [...val.QuoteIDs];
                    } else if (val.QuoteID) {
                        // Backward compatibility: single QuoteID
                        this.selectedQuotes = [val.QuoteID];
                        this.initialSelectedQuotes = [val.QuoteID];
                    } else {
                        this.selectedQuotes = [];
                        this.initialSelectedQuotes = [];
                    }

                    // Load images
                    if (val.Images) {
                        try {
                            this.files = JSON.parse(val.Images);
                        } catch (error) {
                            this.files = [];
                        }
                    } else {
                        this.files = [];
                    }

                    // Load packages
                    if (val.Packages) {
                        try {
                            this.currentPackages = JSON.parse(val.Packages);
                        } catch (error) {
                            this.currentPackages = [];
                        }
                    }

                    // Load fees (FeeObject can be plain array or {WarehouseFees:[], AdditionalFees:[]})
                    if (val.FeeObject) {
                        try {
                            const feeObj = JSON.parse(val.FeeObject);
                            if (Array.isArray(feeObj)) {
                                // Legacy plain array
                                this.currentFees = feeObj;
                                this.additionalFees = [];
                            } else if (feeObj && typeof feeObj === 'object') {
                                // Structured format
                                this.currentFees = feeObj.WarehouseFees || [];
                                this.additionalFees = feeObj.AdditionalFees || [];
                            } else {
                                this.currentFees = [];
                                this.additionalFees = [];
                            }
                        } catch (error) {
                            this.currentFees = [];
                            this.additionalFees = [];
                        }
                    } else {
                        this.currentFees = [];
                        this.additionalFees = [];
                    }

                    this.initializeInvoiceOrderValueFields();

                    // Load quotes for customer if not loaded yet
                    if (val.UID && this.availableQuotes.length === 0) {
                        this.loadQuotesByCustomer(val.UID);
                    }
                }
            },
            immediate: true,
        },
        totalOtherFees(newVal) {
            if (this.currentItem) {
                this.currentItem.OtherFee = newVal;
            }
        },
        totalOtherCosts(newVal) {
            if (this.currentItem) {
                this.currentItem.OtherCost = newVal;
            }
        },
        'currentItem.InvoiceQuantity': function () {
            this.updateInvoiceFromQtyPrice();
        },
        'currentItem.InvoiceUnitPrice': function () {
            this.updateInvoiceFromQtyPrice();
        },
        'currentItem.InvoiceTotalAmount': function () {
            this.updateInvoiceFromTotalVat();
        },
        'currentItem.InvoiceVatRate': function () {
            this.updateInvoiceFromTotalVat();
        },
    },
    methods: {
        display_color,
        hasInvoiceOrderValue(item = this.currentItem) {
            if (!item) return false;
            const values = [
                Number(item.InvoiceQuantity || 0),
                Number(item.InvoiceUnitPrice || 0),
                Number(item.InvoiceTotalAmount || 0),
                Number(item.InvoiceVatRate || 0),
                Number(item.InvoiceTotalAfterVat || 0),
            ];
            return values.some(v => v > 0);
        },
        initializeInvoiceOrderValueFields() {
            if (!this.currentItem) return;

            if (this.currentItem.InvoiceQuantity == null) {
                this.currentItem.InvoiceQuantity = 0;
            }
            if (this.currentItem.InvoiceUnitPrice == null) {
                this.currentItem.InvoiceUnitPrice = 0;
            }
            if (this.currentItem.InvoiceTotalAmount == null && this.currentItem.ProductAmount != null) {
                this.currentItem.InvoiceTotalAmount = this.currentItem.ProductAmount;
            }
            if (this.currentItem.InvoiceVatRate == null && this.currentItem.VatTaxRate != null) {
                this.currentItem.InvoiceVatRate = this.currentItem.VatTaxRate;
            }

            this.updateInvoiceFromTotalVat();
        },
        updateInvoiceFromQtyPrice() {
            if (!this.currentItem) return;

            const quantity = Number(this.currentItem.InvoiceQuantity || 0);
            const unitPrice = Number(this.currentItem.InvoiceUnitPrice || 0);

            if (quantity > 0 && unitPrice > 0) {
                this.currentItem.InvoiceTotalAmount = Math.round(quantity * unitPrice);
            }

            this.updateInvoiceFromTotalVat();
        },
        updateInvoiceFromTotalVat() {
            if (!this.currentItem) return;

            const totalAmount = Number(this.currentItem.InvoiceTotalAmount || 0);
            const vatRate = Number(this.currentItem.InvoiceVatRate || 0);
            const vatAmount = Math.round(totalAmount * vatRate / 100);

            this.currentItem.ProductAmount = totalAmount;
            this.currentItem.VatTaxRate = vatRate;
            this.currentItem.VatTaxAmount = vatAmount;
            this.currentItem.InvoiceTotalAfterVat = totalAmount + vatAmount;

            if (this.hasInvoiceOrderValue()) {
                this.currentItem.IsSplitTax = 1;
            }
        },
        getDisplayText(value, enums, key_field = 'value', value_field = 'name') {
            if (!Array.isArray(enums)) {
                return value || '';
            }

            const item = enums.find(obj => {
                const enumValue = obj[key_field];
                return enumValue === value || String(enumValue) === String(value);
            });

            if (!item) {
                return value || '';
            }

            return item.i18n ? this.$t(item.i18n) : item[value_field];
        },
        goBack() {
            this.$router.go(-1);
        },
        isQuoteSelectable(quoteId) {
            // Check if quote can be selected
            // Allow if: quote has IsLinked !== 1 OR quote was initially selected from database
            const quote = this.availableQuotes.find(q => q.ID === quoteId);

            // If quote not found in available list, allow if it's in initial selection
            if (!quote) {
                return this.initialSelectedQuotes.includes(quoteId);
            }

            // If quote is not linked, it's always selectable
            if (quote.IsLinked !== 1) {
                return true;
            }

            // If quote is linked, only allow if it was initially selected from database
            return this.initialSelectedQuotes.includes(quoteId);
        },
        isQuoteLinked(quoteId) {
            // Check if quote should be displayed as disabled (muted)
            const quote = this.availableQuotes.find(q => q.ID === quoteId);
            if (!quote) return false;
            return quote.IsLinked === 1 && !this.initialSelectedQuotes.includes(quoteId);
        },
        async saveItem() {
            if (!this.$refs.edit_form.validate()) {
                return;
            }

            if (this.currentItem.IsBack && !this.currentItem.BackBillImage) {
                sendErrorNotice('Vui lòng upload ảnh bill thanh toán trước khi tick Đã back');
                return;
            }

            this.saving = true;

            try {
                // Upload pending files first
                if (this.pendingUploads.length > 0) {
                    await this.uploadPendingFiles();
                }

                // Prepare data
                const data = {
                    ...this.currentItem,
                    Images: JSON.stringify(this.files),
                    Packages: JSON.stringify(this.currentPackages),
                    FeeObject: JSON.stringify({ WarehouseFees: this.currentFees, AdditionalFees: this.additionalFees }),
                    IsSplitTax: this.hasInvoiceOrderValue() ? 1 : this.currentItem.IsSplitTax,
                    OtherFee: this.totalOtherFees,
                    OtherCost: this.totalOtherCosts,
                    QuoteIDs: JSON.stringify(this.selectedQuotes)  // Convert array to JSON string
                };

                // Save to store
                this.$store.dispatch("product/setDetail", {
                    data: data,
                    id: data.ID,
                }).then(ret => {
                    ret = ret.data ? ret.data : ret;
                    const productId = this.$route.params.id;
                    if (_.isEmpty(ret)) {
                        sendErrorNotice("Lỗi, không load được dữ liệu.");
                    } else if (!productId || productId === "new") {
                        this.$router.push({ name: 'ProductDetail', params: { id: ret.ID } });
                        this.load_detail();
                    }
                });
                // sendSuccessNotice(this.$t("notifications.save_success"));
            } catch (error) {
                console.error("Save error:", error);
                sendErrorNotice(this.$t("errors.save_failed"));
            } finally {
                this.saving = false;
            }
        },
        addSubItem() {
            let split_packages = [...this.currentPackages];
            split_packages.push({
                Length: 0,
                Width: 0,
                Height: 0,
                Weight: 0,
                NumberOfPack: 0,
            });
            this.currentItem.Packages = JSON.stringify(split_packages);
            this.currentPackages = split_packages;

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { Packages: this.currentItem.Packages },
                    id: this.currentItem.ID,
                });
            }
        },
        deleteSubItem(index) {
            let split_packages = [...this.currentPackages];
            split_packages.splice(index, 1);
            this.currentItem.Packages = JSON.stringify(split_packages);
            this.currentPackages = split_packages;

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { Packages: this.currentItem.Packages },
                    id: this.currentItem.ID,
                });
            }
        },
        updateSubItem(isAddSub = false) {
            if (isAddSub) {
                return;
            }
            this.currentItem.Packages = JSON.stringify(this.currentPackages);

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { Packages: this.currentItem.Packages },
                    id: this.currentItem.ID,
                });
            }
        },
        _buildFeeObject() {
            return { WarehouseFees: this.currentFees, AdditionalFees: this.additionalFees };
        },
        addFeeItem() {
            let fees = [...this.currentFees];
            fees.push({
                FeeType: 1,
                Currency: 'CNY',
                Cost: 0,
                FeeAmount: 0,
                Note: "",
            });
            this.currentFees = fees;
            this.currentItem.FeeObject = JSON.stringify(this._buildFeeObject());

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: {
                        FeeObject: this.currentItem.FeeObject,
                        OtherFee: this.totalOtherFees,
                        OtherCost: this.totalOtherCosts
                    },
                    id: this.currentItem.ID,
                });
            }
        },
        deleteFeeItem(index) {
            let fees = [...this.currentFees];
            fees.splice(index, 1);
            this.currentFees = fees;
            this.currentItem.FeeObject = JSON.stringify(this._buildFeeObject());

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: {
                        FeeObject: this.currentItem.FeeObject,
                        OtherFee: this.totalOtherFees,
                        OtherCost: this.totalOtherCosts
                    },
                    id: this.currentItem.ID,
                });
            }
        },
        updateFeeItem() {
            this.currentItem.FeeObject = JSON.stringify(this._buildFeeObject());

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: {
                        FeeObject: this.currentItem.FeeObject,
                        OtherFee: this.totalOtherFees,
                        OtherCost: this.totalOtherCosts
                    },
                    id: this.currentItem.ID,
                });
            }
        },
        addAdditionalFeeItem() {
            let fees = [...this.additionalFees];
            fees.push({
                FeeType: 1,
                Currency: 'VND',
                Cost: 0,
                FeeAmount: 0,
                Note: "",
            });
            this.additionalFees = fees;
            this.currentItem.FeeObject = JSON.stringify(this._buildFeeObject());

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { FeeObject: this.currentItem.FeeObject },
                    id: this.currentItem.ID,
                });
            }
        },
        deleteAdditionalFeeItem(index) {
            let fees = [...this.additionalFees];
            fees.splice(index, 1);
            this.additionalFees = fees;
            this.currentItem.FeeObject = JSON.stringify(this._buildFeeObject());

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { FeeObject: this.currentItem.FeeObject },
                    id: this.currentItem.ID,
                });
            }
        },
        updateAdditionalFeeItem() {
            this.currentItem.FeeObject = JSON.stringify(this._buildFeeObject());

            if (this.currentItem.ID > 0) {
                this.$store.dispatch("product/setDetail", {
                    data: { FeeObject: this.currentItem.FeeObject },
                    id: this.currentItem.ID,
                });
            }
        },
        fileOpen(f) {
            window.open(this.image_proxy + encodeURIComponent(f.url), "blank");
        },
        openBackBillImage() {
            if (this.currentItem && this.currentItem.BackBillImage) {
                this.backBillPreview = true;
            }
        },
        async uploadBackBillImage(e) {
            const file = e.target.files && e.target.files[0];
            if (!file) {
                return;
            }

            const validation = fileUploadService.validateFile(file, ['jpg', 'jpeg', 'png', 'gif'], 10);
            if (!validation.valid) {
                sendErrorNotice(validation.message);
                if (this.$refs.backBillInput) {
                    this.$refs.backBillInput.value = null;
                }
                return;
            }

            this.backBillUploading = true;
            try {
                const result = await fileUploadService.uploadSingleFile(file, 'bill');
                if (result.success && result.data) {
                    this.currentItem.BackBillImage = result.data.url;
                    this.currentItem.BackBillImagePath = result.data.path;
                    sendSuccessNotice('Đã upload ảnh bill thanh toán');
                } else {
                    sendErrorNotice(result.message || 'Lỗi upload ảnh bill thanh toán');
                }
            } catch (error) {
                console.error('Upload back bill error:', error);
                sendErrorNotice('Lỗi upload ảnh bill thanh toán');
            } finally {
                this.backBillUploading = false;
                if (this.$refs.backBillInput) {
                    this.$refs.backBillInput.value = null;
                }
            }
        },
        async removeBackBillImage() {
            if (!confirm('Bạn có chắc chắn muốn xóa ảnh bill thanh toán?')) {
                return;
            }

            if (this.currentItem.BackBillImagePath) {
                try {
                    await fileUploadService.deleteFile(this.currentItem.BackBillImagePath);
                } catch (error) {
                    console.error('Delete back bill error:', error);
                }
            }

            this.currentItem.BackBillImage = '';
            this.currentItem.BackBillImagePath = '';
        },
        async fileRemove(index) {
            if (!confirm('Bạn có chắc chắn muốn xóa ảnh này?')) {
                return;
            }

            const fileToRemove = this.files[index];

            // Delete from storage if it's not a local file
            if (fileToRemove.path) {
                try {
                    await fileUploadService.deleteFile(fileToRemove.path);
                    sendSuccessNotice('Đã xóa file trên storage');
                } catch (error) {
                    console.error("Error deleting file from storage:", error);
                    sendErrorNotice('Có lỗi khi xóa file trên storage, nhưng file đã được gỡ khỏi sản phẩm');
                }
            }

            // Remove from local array
            this.files.splice(index, 1);

            // Update database if product exists
            if (this.currentItem && this.currentItem.ID > 0) {
                this.currentItem.Images = JSON.stringify(this.files);
                try {
                    await this.$store.dispatch("product/setDetail", {
                        data: { Images: this.currentItem.Images },
                        id: this.currentItem.ID,
                    });
                    sendSuccessNotice('Đã cập nhật danh sách ảnh');
                } catch (error) {
                    console.error("Error updating product images:", error);
                    sendErrorNotice('Có lỗi khi cập nhật danh sách ảnh');
                }
            }
        },
        uploadFile(e) {
            const file = e.target.files[0];
            this.pendingUploads.push(file);
            this.files.push({
                local: 1,
                name: file.name,
                url: URL.createObjectURL(file),
            });
            this.$refs.fileInput.value = null;
        },
        handlePaste(e) {
            const items = e.clipboardData.items;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const blob = items[i].getAsFile();
                    const timestamp = new Date().getTime();
                    const fileName = `paste-${timestamp}.png`;

                    // Create a File object with proper name
                    const file = new File([blob], fileName, { type: blob.type });

                    this.pendingUploads.push(file);
                    this.files.push({
                        local: 1,
                        name: fileName,
                        url: URL.createObjectURL(file),
                    });

                    sendSuccessNotice('Đã dán ảnh từ clipboard');
                    e.preventDefault();
                    break;
                }
            }
        },
        async uploadPendingFiles() {
            this.files = _.filter(this.files, (p) => { return p.local !== 1 });
            this.uploading = this.pendingUploads.length;

            for (let index = 0; index < this.pendingUploads.length; index++) {
                const file = this.pendingUploads[index];

                // Validate file using service
                const validation = fileUploadService.validateFile(file, ['jpg', 'jpeg', 'png', 'gif'], 10);
                if (!validation.valid) {
                    sendErrorNotice(validation.message);
                    this.uploading = this.uploading - 1;
                    continue;
                }

                try {
                    // Upload through backend proxy
                    const result = await fileUploadService.uploadSingleFile(file, 'bill');
                    if (result.success) {
                        this.files.push({
                            'name': result.data.name,
                            'url': result.data.url,
                            'path': result.data.path,
                            'size': result.data.size
                        });
                    } else {
                        sendErrorNotice(result.message);
                    }
                } catch (error) {
                    console.error('Upload error:', error);
                    sendErrorNotice('Lỗi upload file!');
                }

                this.uploading = this.uploading - 1;
            }

            // Clear pending uploads after all files are processed
            this.pendingUploads = [];
        },
        loadQuotesByCustomer(customerId) {
            if (!customerId) {
                this.availableQuotes = [];
                return;
            }

            this.loadingQuotes = true;
            this.$store
                .dispatch("quote/getQuotesByCustomer", customerId)
                .then((quotes) => {
                    this.availableQuotes = quotes || [];
                })
                .catch((error) => {
                    console.error("Error loading quotes:", error);
                    this.availableQuotes = [];
                })
                .finally(() => {
                    this.loadingQuotes = false;
                });
        },
        onCustomerChange() {
            this.selectedQuotes = [];
            if (this.currentItem.UID) {
                this.loadQuotesByCustomer(this.currentItem.UID);
            } else {
                this.availableQuotes = [];
            }
        },
        removeQuote(quoteId) {
            const index = this.selectedQuotes.indexOf(quoteId);
            if (index > -1) {
                this.selectedQuotes.splice(index, 1);
            }
        },
        quoteFilter(item, queryText, itemText) {
            if (!queryText) return true;

            const searchText = queryText.toLowerCase();
            const hsCode = (item.HsCode || "").toLowerCase();
            const productInfo = (item.ProductInfo || "").toLowerCase();

            return hsCode.includes(searchText) || productInfo.includes(searchText);
        },
        load_detail() {
            const productId = this.$route.params.id;
            if (productId && productId !== "new") {
                // Load existing product
                this.$store.dispatch("product/getDetail", productId);
            } else {
                // Create new product
                this.$store.commit("product/setDetail", {
                    data: {
                        ID: 0,
                        UID: null,
                        StoreID: null,
                        PartnerName: "",
                        QuoteID: null,
                        QuoteIDs: [],  // Initialize empty array for new product
                        ProductNameCN: "",
                        ProductType: null,
                        OtherFee: 0,
                        OtherCost: 0,
                        ExchangeRate: 0,
                        Weight: 0,
                        Volume: 0,
                        NumberOfPack: 0,
                        Note: "",
                        PriceType: null,
                        WeightPrice: 0,
                        VolumePrice: 0,
                        IsSplitTax: false,
                        WeightCost: 0,
                        VolumeCost: 0,
                        ServiceFeeAmount: 0,
                        ImportTaxAmount: 0,
                        VatTaxAmount: 0,
                        TotalAmount: 0,
                        SelfFee: 0,
                        TotalProfit: 0,
                        IsBack: 0,
                        BackBillImage: "",
                        BackBillImagePath: "",
                        Images: "[]",
                        Packages: "[]",
                        FeeObject: "[]",
                    }, id: 0
                });
            }
        }
    },
    mounted() {
        // Listen to PageHeader save event
        this.$root.$on("REQUEST_SAVE_DATA", this.saveItem);
        this.$root.$on("REQUEST_REFRESH_DATA", this.load_detail);
        // Load required data
        this.$store.dispatch("commons/getAllCustomer");
        this.$store.dispatch("storage/getList");
        this.$store.dispatch("productType/getList");
        this.load_detail();
    },
    beforeDestroy() {
        this.$root.$off("REQUEST_SAVE_DATA");
        this.$root.$off("REQUEST_REFRESH_DATA");
    },
};
</script>

<style scoped>
.sticky-card {
    position: sticky;
    top: 80px;
}

.thumbnail-img {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: box-shadow 0.2s;
}

.thumbnail-img:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
}

.disabled-quote-item {
    opacity: 0.5;
    background-color: rgba(0, 0, 0, 0.02) !important;
}

.back-bill-thumb-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.back-bill-remove-btn {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #fff;
    border-radius: 50%;
}
</style>
